import { useState } from 'react';
import { AlertTriangle, Loader2, Plus, Users, X } from 'lucide-react';
import {
  audienceConditionOptions,
  audienceParamFields,
  audienceSchema,
  defaultAudience,
  type Audience,
  type AudienceConditionType,
  type AudienceFilterField,
  type AudienceParamField,
} from '@vagusx/shared';
import type { AudienceCatalogEntry } from '@vagusx/clients';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LocationPicker } from '@/components/custom/location-picker';
import { ProviderPicker } from '@/components/custom/provider-picker';
import { MultiSelect } from '@/components/custom/multi-select';
import { useAppointmentTypes } from '@/hooks/use-appointment-types';
import { useDebounce } from '@/hooks/use-debounce';
import { useAudiencePreview } from '@/hooks/use-outreach';

/**
 * Who qualifies — a condition from the catalog plus its parameters.
 *
 * Everything renderable here is derived: the catalog comes from the API, the
 * parameter list and its ranges come from `audienceSchema`, and which filters
 * are offered comes from the condition's own `supportedFilters`. Adding an
 * audience does not mean editing this file.
 */

interface AudienceSectionProps {
  audience: Audience;
  /** The campaign has a call step: counted on the same key as the "Right now" panel. */
  placesCalls: boolean;
  onChange: (audience: Audience) => void;
  catalog: AudienceCatalogEntry[] | undefined;
}

function paramValue(audience: Audience, key: string): number | boolean | undefined {
  return (audience.params as Record<string, number | boolean>)[key];
}

/**
 * Writes one parameter and re-parses.
 *
 * Re-parsing is what keeps this honest: a value the schema would reject is
 * dropped here rather than accepted into state and rejected later by publish,
 * which would leave the author staring at a form that looks fine.
 */
function setParam(audience: Audience, key: string, value: number | boolean): Audience {
  const next = {
    ...audience,
    params: { ...(audience.params as Record<string, unknown>), [key]: value },
  };
  const parsed = audienceSchema.safeParse(next);
  return parsed.success ? parsed.data : audience;
}

function ParamField({
  field,
  audience,
  onChange,
}: {
  field: AudienceParamField;
  audience: Audience;
  onChange: (audience: Audience) => void;
}): React.ReactElement {
  if (field.kind === 'toggle') {
    return (
      <div className="flex items-start justify-between gap-4 py-1">
        <div className="min-w-0">
          <Label htmlFor={`param-${field.key}`} className="font-normal">
            {field.label}
          </Label>
          {field.help && <p className="mt-0.5 text-xs text-muted-foreground">{field.help}</p>}
        </div>
        <Switch
          id={`param-${field.key}`}
          checked={paramValue(audience, field.key) === true}
          onCheckedChange={(checked) => onChange(setParam(audience, field.key, checked))}
        />
      </div>
    );
  }

  const current = paramValue(audience, field.key);
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <Label htmlFor={`param-${field.key}`} className="font-normal">
        {field.label}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={`param-${field.key}`}
          type="number"
          className="w-20"
          min={field.min}
          max={field.max}
          value={typeof current === 'number' ? current : field.min}
          onChange={(e) => {
            const parsed = Number(e.target.value);
            if (Number.isFinite(parsed)) onChange(setParam(audience, field.key, parsed));
          }}
        />
        <span className="w-14 text-sm text-muted-foreground">{field.unit}</span>
      </div>
    </div>
  );
}

const FIELD_LABELS: Record<AudienceFilterField, string> = {
  location: 'Locations',
  provider: 'Providers',
  appointmentType: 'Appointment types',
};

/**
 * One narrowing dimension the author has chosen to add.
 *
 * Rows exist only once added. Rendering every supported dimension up front put
 * three rows of "Any location / Any provider / Any appointment type" on a
 * screen for a campaign that narrows by none of them — noise that reads as
 * configuration.
 */
function FilterRow({
  field,
  values,
  onChange,
  onRemove,
}: {
  field: AudienceFilterField;
  values: string[];
  onChange: (values: string[]) => void;
  onRemove: () => void;
}): React.ReactElement {
  const { data: appointmentTypes } = useAppointmentTypes();

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="font-normal text-muted-foreground">{FIELD_LABELS[field]}</Label>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          aria-label={`Remove the ${FIELD_LABELS[field].toLowerCase()} filter`}
          onClick={onRemove}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      {field === 'location' && (
        <LocationPicker multiple selected={values} onChange={onChange} placeholder="Choose…" />
      )}
      {field === 'provider' && (
        <ProviderPicker multiple selected={values} onChange={onChange} placeholder="Choose…" />
      )}
      {field === 'appointmentType' && (
        <MultiSelect
          options={(appointmentTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
          selected={values}
          onChange={onChange}
          placeholder="Choose…"
        />
      )}
    </div>
  );
}

export function AudienceSection({
  audience,
  placesCalls,
  onChange,
  catalog,
}: AudienceSectionProps): React.ReactElement {
  const entry = catalog?.find((c) => c.type === audience.type);
  const supported = entry?.supportedFilters ?? [];
  const fields = audienceParamFields(audience.type);

  /**
   * Counting is a population-scale query — the same one the reconciler runs —
   * so it must not fire per keystroke. Typing "180" into a parameter would
   * otherwise issue three of them, one per intermediate value, none cancelled.
   */
  const debouncedAudience = useDebounce(audience, 400);
  const preview = useAudiencePreview(debouncedAudience, placesCalls);

  /**
   * Dimensions added but not yet given a value.
   *
   * A criterion with no values fails `min(1)`, so an empty one cannot live in
   * the spec — but the author needs the row on screen to choose values INTO.
   * Holding those here keeps the spec always-valid without making "add a
   * filter" a two-step dance.
   */
  const [pending, setPending] = useState<AudienceFilterField[]>([]);

  const valuesFor = (field: AudienceFilterField): string[] =>
    audience.filters.find((c) => c.field === field)?.values ?? [];

  // Existing criteria first, in the condition's own dimension order, then any
  // row still waiting for a value.
  const shown = supported.filter(
    (f) => audience.filters.some((c) => c.field === f) || pending.includes(f)
  );
  const available = supported.filter((f) => !shown.includes(f));

  /**
   * Writes one dimension. Clearing it drops the criterion entirely rather than
   * leaving an empty one — an empty criterion fails `min(1)` and would make the
   * campaign unpublishable from a control that looked like it had been reset.
   */
  const setField = (field: AudienceFilterField, values: string[]): void => {
    const existing = audience.filters.findIndex((c) => c.field === field);
    let next: Audience['filters'];

    if (!values.length) {
      next = audience.filters.filter((c) => c.field !== field);
    } else if (existing === -1) {
      next = [...audience.filters, { field, values }];
    } else {
      // Replace in place. Appending instead would reorder the array on every
      // edit, which marks the spec dirty for a semantically identical change
      // and shuffles the order `describeFilters` reads the campaign back in.
      next = audience.filters.map((c, i) => (i === existing ? { field, values } : c));
    }

    const parsed = audienceSchema.safeParse({ ...audience, filters: next });
    if (parsed.success) onChange(parsed.data);
  };

  const removeField = (field: AudienceFilterField): void => {
    setPending((rows) => rows.filter((f) => f !== field));
    setField(field, []);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Patients</Label>
        <Select
          value={audience.type}
          onValueChange={(type) => {
            // The new condition supports different dimensions, so a row still
            // waiting for a value under the old one is meaningless here.
            setPending([]);
            onChange(defaultAudience(type as AudienceConditionType));
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {audienceConditionOptions().map((option) => (
              <SelectItem key={option.type} value={option.type}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        {fields.map((field) => (
          <ParamField key={field.key} field={field} audience={audience} onChange={onChange} />
        ))}
      </div>

      {/*
        Only the dimensions this condition can actually answer are offered. A
        location filter on `new_patients` parses cleanly and means nothing —
        those patients have no appointment to carry a location — so the API
        rejects it at publish. Not offering it is the same rule, enforced where
        the author is looking.
      */}
      {supported.length > 0 && (
        <div className="space-y-3 border-t pt-3">
          {shown.map((field) => (
            <FilterRow
              key={field}
              field={field}
              values={valuesFor(field)}
              onChange={(values) => setField(field, values)}
              onRemove={() => removeField(field)}
            />
          ))}

          {available.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs">
                  <Plus className="mr-1 h-3 w-3" />
                  Add a filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {available.map((field) => (
                  <DropdownMenuItem
                    key={field}
                    onSelect={() => setPending((rows) => [...rows, field])}
                  >
                    {FIELD_LABELS[field]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      {/*
        The live count, from the same query the reconciler runs. `aboveCeiling`
        is surfaced loudly because a preview that says "142,000 match" while the
        reconciler silently refuses the campaign is worse than no preview.
      */}
      <div className="rounded-md border bg-muted/40 p-3">
        <div className="flex items-start gap-2">
          {preview.isFetching ? (
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 text-sm">
            {preview.data ? (
              <>
                <div>
                  <span className="font-medium tabular-nums">{preview.data.patients}</span>{' '}
                  {preview.data.patients === 1 ? 'patient matches' : 'patients match'} right now
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{preview.data.description}</p>
              </>
            ) : preview.isError ? (
              <span className="text-muted-foreground">Could not count this audience.</span>
            ) : (
              <span className="text-muted-foreground">Counting…</span>
            )}
          </div>
        </div>

        {preview.data?.aboveCeiling && (
          <div className="mt-2 flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Too large to run. The reconciler refuses an audience this size on every tick, so the
              campaign would never call anyone. Narrow it before publishing.
            </span>
          </div>
        )}

        {preview.data?.problems.map((problem) => (
          <div key={problem} className="mt-2 flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{problem}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
